import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'react-toastify';

import { GT_COLORS } from '../groups-teams/shared/constants';
import { NotificationRulesService } from '../../services/notification-rules.service';
import { RoutingOverview } from '../../types/notification-rules.types';

/**
 * Call on Dispatch: who POM calls or emails when EMS is actually sent to an SOS.
 *
 * There is no separate on/off switch. Saving an edited, nonempty list turns it
 * on and saving an empty list turns it off. Stored contacts remain visible even
 * while disabled; displaying or cancelling edits must not change that setting.
 *
 * Phone numbers must be +1 followed by ten digits, which is what the API's
 * catalog validation enforces, so the same rule is applied here before saving.
 */

const isValidUsPhone = (v: string) => /^\+1\d{10}$/.test(v.trim());
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const formatUsPhone = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').replace(/^1/, '').slice(0, 10);
    return `+1${digits}`;
};

interface Props {
    overview: RoutingOverview;
    onSaved: (next: RoutingOverview) => void;
    /** Shows the contacts as a plain list, for people who cannot change them. */
    readOnly?: boolean;
}

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } };

export const DispatchContactsEditor: React.FC<Props> = ({ overview, onSaved, readOnly = false }) => {
    // Enabled controls notification delivery, not whether saved contacts exist.
    const stored = overview.dispatch;

    const [phones, setPhones] = useState<string[]>(stored.phones);
    const [emails, setEmails] = useState<string[]>(stored.emails);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setPhones(stored.phones);
        setEmails(stored.emails);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overview]);

    const badPhones = phones.filter((p) => !isValidUsPhone(p));
    const badEmails = emails.filter((e) => !isValidEmail(e));
    const dirty = phones.join('|') !== stored.phones.join('|') || emails.join('|') !== stored.emails.join('|');

    const save = async () => {
        if (badPhones.length || badEmails.length) return;
        setSaving(true);
        try {
            onSaved(await NotificationRulesService.updateDispatchContacts({
                enabled: phones.length + emails.length > 0,
                phones,
                emails,
            }));
            toast.success('Saved.');
        } catch {
            toast.error('Could not save. Nothing was changed.');
        } finally {
            setSaving(false);
        }
    };

    const list = (
        items: string[],
        setItems: (next: string[]) => void,
        invalid: (v: string) => boolean,
        error: string,
        placeholder: string,
        format: (v: string) => string,
        removeLabel: string,
    ) => items.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', maxWidth: 420 }}>
            <TextField
                size="small"
                fullWidth
                value={value}
                placeholder={placeholder}
                disabled={saving}
                error={invalid(value)}
                helperText={invalid(value) ? error : ''}
                onChange={(e) => {
                    const next = [...items];
                    next[index] = format(e.target.value);
                    setItems(next);
                }}
                sx={fieldSx}
            />
            <IconButton
                size="small"
                disabled={saving}
                aria-label={removeLabel}
                onClick={() => setItems(items.filter((_, i) => i !== index))}
            >
                <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
        </Box>
    ));

    const disabledStatus = !overview.dispatch.enabled && (
        <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>
            Call on Dispatch is disabled. Saved contacts are not notified.
            {!readOnly && ' Saving a nonempty contact list will enable it.'}
        </Typography>
    );

    if (readOnly) {
        const listed = [...stored.phones, ...stored.emails];
        return (
            <Box sx={{ display: 'grid', gap: '6px' }}>
                {disabledStatus}
                {listed.length
                    ? listed.map((value) => (
                        <Typography key={value} sx={{ fontSize: '14px' }}>{value}</Typography>
                    ))
                    : (
                        <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>No one is listed.</Typography>
                    )}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: '12px' }}>
            {disabledStatus}
            {phones.length + emails.length === 0 && (
                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>
                    No one yet. Most organizations leave this empty.
                </Typography>
            )}

            {phones.length > 0 && (
                <Box sx={{ display: 'grid', gap: '8px' }}>
                    {list(phones, setPhones, (v) => !isValidUsPhone(v), 'Use +1 followed by 10 digits',
                        '+1', formatUsPhone, 'Remove phone number')}
                </Box>
            )}
            {emails.length > 0 && (
                <Box sx={{ display: 'grid', gap: '8px' }}>
                    {list(emails, setEmails, (v) => !isValidEmail(v), 'Enter a valid email address',
                        'name@example.com', (v) => v, 'Remove email address')}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={saving || phones.length >= 10}
                    onClick={() => setPhones([...phones, '+1'])}
                    sx={{ textTransform: 'none' }}
                >
                    Phone number
                </Button>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={saving || emails.length >= 10}
                    onClick={() => setEmails([...emails, ''])}
                    sx={{ textTransform: 'none' }}
                >
                    Email
                </Button>
            </Box>

            {dirty && (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={saving || badPhones.length > 0 || badEmails.length > 0}
                        onClick={save}
                        sx={{ textTransform: 'none' }}
                    >
                        Save
                    </Button>
                    <Button
                        size="small"
                        disabled={saving}
                        onClick={() => { setPhones(stored.phones); setEmails(stored.emails); }}
                        sx={{ textTransform: 'none', color: GT_COLORS.textMuted }}
                    >
                        Cancel
                    </Button>
                </Box>
            )}
        </Box>
    );
};
